import React from 'react';
import {
  X, Rocket,
  Bot, HardDrive, XCircle, Sparkles, Zap, Bug, Wrench, Database,
  LayoutDashboard, BarChart2, Layers, FileSearch, Clock, MessageSquare, StickyNote, CheckCircle,
  ScanSearch, Users, TrendingUp, Fingerprint,
  Crosshair, CalendarRange, GitPullRequest, ShieldCheck, ExternalLink, FlaskConical,
} from 'lucide-react';
import { CHANGELOG } from '../changelog';

// Toute icone citee dans changelog.js doit etre listee ici : FeatureCard
// retombe silencieusement sur Sparkles pour un nom absent de cette map.
const ICON_MAP = {
  Bot, HardDrive, XCircle, Sparkles, Zap, Bug, Wrench, Database, Rocket,
  LayoutDashboard, BarChart2, Layers, FileSearch, Clock, MessageSquare, StickyNote, CheckCircle,
  ScanSearch, Users, TrendingUp, Fingerprint,
  // Citees par les notes 2.3 et 2.4 : sans elles, cinq entrees affichaient
  // la meme etoile generique que tout le reste.
  Crosshair, CalendarRange, GitPullRequest, ShieldCheck, ExternalLink, FlaskConical,
};

function FeatureCard({ entry }) {
  const Icon = ICON_MAP[entry.icon] || Sparkles;
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${entry.color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        {/* La zone concernée en premier : le dashboard compte une dizaine de
            pages, et un titre comme « La plateforme affichée reflète le run
            réel » ne dit pas laquelle change. Sans ce repère, le lecteur doit
            deviner si la nouveauté le concerne. */}
        <p className="text-sm font-semibold text-gray-800">
          {entry.scope && (
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mr-2">
              {entry.scope}
            </span>
          )}
          {entry.title}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{entry.description}</p>
      </div>
    </div>
  );
}

export default function WhatsNewModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-modal-in">
        {/* Header gradient */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4 text-white flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Rocket className="w-5 h-5 text-indigo-200" />
              <span className="font-bold text-base">Nouveautés</span>
            </div>
            <p className="text-xs text-indigo-200">Découvrez ce qui a changé dans cette version</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-white/20 transition flex-shrink-0 ml-4"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh] px-4 py-3 space-y-5">
          {CHANGELOG.map((release) => (
            <div key={release.version}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                  v{release.version}
                </span>
                <span className="text-xs text-gray-400">{release.date}</span>
              </div>
              <div className="space-y-1">
                {release.entries.map((entry, i) => (
                  <FeatureCard key={i} entry={entry} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition shadow-sm"
          >
            C'est noté !
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
        .animate-modal-in {
          animation: modal-in 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
